import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {CopilotCustomInstructions} from '@github-ui/copilot-chat-types'
import type {InstructionScope} from '@github-ui/ahp-session-client/contributed-instructions'
import {fetchRepo} from '@github-ui/copilot-shared-ui/repos/fetch-copilot-chat-repo'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {useCallback, useEffect, useRef} from 'react'

export function repositoryInstructionIds(attachments: readonly MessageAttachment[]): number[] {
  const ids = new Set<number>()
  for (const attachment of attachments) {
    const detail = attachment._meta?.['copilot.attachmentDetail']
    if (
      typeof detail !== 'object' ||
      detail === null ||
      !('type' in detail) ||
      detail.type !== 'github_repository' ||
      !('repo' in detail)
    ) {
      continue
    }
    const repo = detail.repo
    if (typeof repo !== 'object' || repo === null || !('id' in repo)) continue
    if (typeof repo.id === 'number' && Number.isSafeInteger(repo.id) && repo.id > 0) ids.add(repo.id)
  }
  return [...ids]
}

function isCustomInstruction(value: unknown): value is CopilotCustomInstructions {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value.type === 'Repository' || value.type === 'Organization') &&
    'owner' in value &&
    typeof value.owner === 'string' &&
    !!value.owner.trim() &&
    'prompt' in value &&
    typeof value.prompt === 'string' &&
    !!value.prompt.trim()
  )
}

export function useSelectedRepositoryInstructions(): {
  resolve: (
    repositoryIds: readonly number[],
    instructionScope?: InstructionScope,
  ) => Promise<CopilotCustomInstructions[]>
} {
  const lifecycle = useRef({
    mounted: false,
    generation: 0,
    cache: new Map<number, CopilotCustomInstructions[]>(),
    pending: new Map<number, Promise<CopilotCustomInstructions[]>>(),
  })

  useEffect(() => {
    const current = lifecycle.current
    current.mounted = true
    return () => {
      current.mounted = false
      current.generation++
      current.cache.clear()
      current.pending.clear()
    }
  }, [])

  const resolve = useCallback(
    async (
      repositoryIds: readonly number[],
      instructionScope: InstructionScope = 'repository',
    ): Promise<CopilotCustomInstructions[]> => {
      const enabled = () => copilotLocalStorage.getRepoCustomInstructionsState()
      const current = lifecycle.current
      if (!current.mounted || !enabled()) return []
      const generation = current.generation
      const isCurrent = () => current.mounted && current.generation === generation && enabled()

      const results = await Promise.all(
        [...new Set(repositoryIds)].map(async id => {
          const cached = current.cache.get(id)
          if (cached) return cached
          const pending = current.pending.get(id)
          if (pending) return pending

          const request = (async () => {
            try {
              const repository = await fetchRepo(id)
              const instructions = Array.isArray(repository.customInstructions)
                ? repository.customInstructions.filter(isCustomInstruction)
                : []
              if (isCurrent()) current.cache.set(id, instructions)
              return instructions
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('[copilot-composer] failed to fetch selected repository custom instructions', id, error)
              return []
            } finally {
              if (current.generation === generation) current.pending.delete(id)
            }
          })()
          current.pending.set(id, request)
          return request
        }),
      )

      return isCurrent()
        ? results.flat().filter(instruction => instructionScope === 'repository' || instruction.type === 'Organization')
        : []
    },
    [],
  )

  return {resolve}
}
